import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  MessageCircle, ArrowRight, Phone, Mail, MapPin, Clock, Send, 
  ChevronRight, Crosshair, Activity, Zap, ShieldCheck, Layout, Users, 
  CheckCircle, Globe, Search, MoreVertical, LayoutDashboard, HelpCircle
} from 'lucide-react';

import { BrandLink } from '../components/BrandLogo';
import Seo from '../components/Seo';
import WebsiteFooter from '../components/WebsiteFooter';
import { cn } from '../utils/cn';
import api from '../services/api';
import alertService from '../utils/alert';

/**
 * AD PILOT Enterprise Contact Portal
 * Fluid high-density architecture (SLDS Professional)
 */
export default function ContactPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector(s => s.auth);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;

    setSending(true);
    try {
      const res = await api.post('/public/contact', form);
      if (res.data.success) {
        setSubmitted(true);
        alertService.toast('success', 'Message sent');
      }
    } catch (err) {
      alertService.error('Transmission Error', err.response?.data?.error || 'Failed to synchronize with support nodes.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[var(--slds-color-text-main)] overflow-x-hidden selection:bg-[var(--slds-color-brand)]/20 font-sans">
      <Seo
        title="Contact WaapLeads CRM | Sales, Support & Deployment Help"
        description="Contact WaapLeads CRM for WhatsApp Business automation, Meta Ads setup, AI CRM onboarding, enterprise deployment guidance, and support."
        pathname="/contact"
      />
      
      {/* ── Navbar (Fluid Alignment) ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[var(--slds-color-border)] h-14 shadow-sm">
          <div className="w-full px-6 lg:px-20 h-full flex items-center justify-between">
              <div onClick={() => navigate('/')}>
                <BrandLink to="/" size={36} subtitle="AI Revenue Command" />
              </div>
              
              <div className="hidden md:flex items-center gap-8">
                  <Link to="/#features" className="text-[13px] font-semibold hover:text-[var(--slds-color-brand)] transition-colors">Solutions</Link>
                  <Link to="/#pricing" className="text-[13px] font-semibold hover:text-[var(--slds-color-brand)] transition-colors">Pricing</Link>
                  <Link to="/contact" className="text-[13px] font-semibold text-[var(--slds-color-brand)]">Company</Link>
              </div>

              <div className="flex items-center gap-4">
                  {isAuthenticated ? (
                      <Link to="/dashboard" className="slds-button-brand h-8 px-4 text-[12px] flex items-center justify-center">Dashboard</Link>
                  ) : (
                      <>
                          <Link to="/login" className="text-[13px] font-semibold px-3">Login</Link>
                          <Link to="/register" className="slds-button-brand h-8 px-4 text-[12px] flex items-center justify-center">Try for Free</Link>
                      </>
                  )}
              </div>
          </div>
      </nav>

      <main className="pt-32">
        {/* Contact Hero (Fluid, Left Aligned) */}
        <section className="pb-24 px-6 lg:px-20 border-b border-[var(--slds-color-border)] bg-[var(--slds-color-bg)]">
            <div className="w-full space-y-6 pt-12">
               <div className="px-4 py-1 rounded-full bg-white border border-[var(--slds-color-border)] text-[11px] font-bold text-[var(--slds-color-brand)] uppercase tracking-widest inline-flex items-center gap-2 shadow-sm">
                   <Crosshair size={14} /> Industrial Sales Engineering Support
               </div>
               <h1 className="text-4xl lg:text-6xl font-bold tracking-tighter leading-tight text-[var(--slds-color-text-main)] max-w-4xl">
                  Synchronize Your Operations<br /> with Our <span className="text-[var(--slds-color-brand)]">Solutions Command</span>
               </h1>
               <p className="text-[18px] font-medium text-[var(--slds-color-text-secondary)] leading-relaxed max-w-2xl">
                  The WaapLeads engineering team is ready to assist with enterprise-grade deployments, official Meta API scaling, and high-density CRM transformations.
               </p>
            </div>
        </section>

        {/* Global Connection Nodes (Fluid Grid) */}
        <section className="py-24 px-6 lg:px-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 bg-white">
           {[
             { icon: MessageCircle, color: 'text-[#04844B]', label: 'Primary Messaging Node', val: 'Chat via WhatsApp', desc: 'Accelerated response protocol', link: 'https://wa.me/917392080372' },
             { icon: Mail, color: 'text-[#0176D3]', label: 'Transmission Support', val: 'info@waapcoders.in', desc: 'Sync within 24 operational hours' },
             { icon: Phone, color: 'text-[#5A1BA9]', label: 'Voice Interaction', val: '+91 73920 80372', desc: 'Mon—Sat · 10 AM — 7 PM IST' },
             { icon: Globe, color: 'text-[#056764]', label: 'Inbound Registry', val: 'Send Message', desc: 'Use the official portal below' },
           ].map((c, i) => (
             <div key={i} className="flex flex-col items-start space-y-4 p-8 rounded-lg border border-[var(--slds-color-border)] bg-[var(--slds-color-bg)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.16)] transition-all group">
                <div className={cn("w-12 h-12 rounded-[var(--slds-radius)] bg-white border border-[var(--slds-color-border)] flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.16)]", c.color)}>
                   <c.icon size={22} strokeWidth={2} />
                </div>
                <div>
                   <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--slds-color-text-secondary)] mb-1">{c.label}</p>
                   <h3 className="text-lg font-bold text-[var(--slds-color-text-main)] group-hover:text-[var(--slds-color-brand)]">{c.val}</h3>
                   <p className="text-[12px] font-medium text-[var(--slds-color-text-secondary)]">{c.desc}</p>
                </div>
             </div>
           ))}
        </section>

        {/* Inquiry Matrix (Fluid Split Layout) */}
        <section className="py-24 px-6 lg:px-20 border-t border-[var(--slds-color-border)] bg-[var(--slds-color-bg)] grid grid-cols-1 lg:grid-cols-12 gap-20">
           
           <div className="lg:col-span-8 space-y-12">
              <div className="space-y-4">
                 <h2 className="text-3xl font-bold text-[var(--slds-color-text-main)]">Send Message</h2>
                 <p className="text-[14px] font-medium text-[var(--slds-color-text-secondary)]">Provide the essential signal parameters for our support matrix to process.</p>
              </div>

              {submitted ? (
                 <div className="bg-white p-16 rounded-lg border-2 border-[#9FD9B4] flex flex-col items-center justify-center text-center space-y-6 shadow-[0_2px_8px_rgba(0,0,0,0.16)] animate-in zoom-in duration-300">
                    <div className="w-20 h-20 rounded-full bg-[#E3F3E8] flex items-center justify-center text-[#04844B] shadow-inner"><CheckCircle size={48} /></div>
                    <div className="space-y-2">
                       <h3 className="text-2xl font-bold text-[var(--slds-color-text-main)] uppercase tracking-tighter">Transmission Synchronized</h3>
                       <p className="text-[14px] font-medium text-[var(--slds-color-text-secondary)] max-w-sm">Your operational inquiry has been successfully dispatched to our resolution nodes. Please check your registry (Email).</p>
                    </div>
                    <button onClick={() => setSubmitted(false)} className="slds-button-brand h-10 px-8 font-bold text-sm uppercase tracking-widest">Reset Interface</button>
                 </div>
              ) : (
                <form className="space-y-8 bg-white p-12 rounded-lg border border-[var(--slds-color-border)] shadow-[0_2px_8px_rgba(0,0,0,0.16)]" onSubmit={handleSubmit}>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-3">
                         <label className="text-[12px] font-bold text-[var(--slds-color-text-secondary)] uppercase tracking-widest">Entity Full Name</label>
                         <input name="name" value={form.name} onChange={handleChange} required placeholder="e.g., Alex Johnson" className="w-full h-10 bg-[var(--slds-color-bg)] border border-[var(--slds-color-border)] rounded-[var(--slds-radius)] px-4 text-sm font-bold focus:border-[var(--slds-color-brand)] outline-none" />

                      </div>
                      <div className="space-y-3">
                         <label className="text-[12px] font-bold text-[var(--slds-color-text-secondary)] uppercase tracking-widest">Transmission Email</label>
                         <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="hub@enterprise.com" className="w-full h-10 bg-[var(--slds-color-bg)] border border-[var(--slds-color-border)] rounded-[var(--slds-radius)] px-4 text-sm font-bold focus:border-[var(--slds-color-brand)] outline-none" />
                      </div>
                   </div>
                   
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-3">
                         <label className="text-[12px] font-bold text-[var(--slds-color-text-secondary)] uppercase tracking-widest">Subject Classification</label>
                         <select name="subject" value={form.subject} onChange={handleChange} className="w-full h-10 bg-[var(--slds-color-bg)] border border-[var(--slds-color-border)] rounded-[var(--slds-radius)] px-4 text-sm font-bold focus:border-[var(--slds-color-brand)] outline-none">
                             <option value="">Choose a topic…</option>
                             <option>Financial Node Sync</option>
                             <option>Commercial Operations</option>
                             <option>Technical Infrastructure</option>
                             <option>Strategic Roadmap</option>
                         </select>
                      </div>
                      <div className="space-y-3">
                         <label className="text-[12px] font-bold text-[var(--slds-color-text-secondary)] uppercase tracking-widest">Direct Signal Link (Phone)</label>
                         <input name="phone" value={form.phone} onChange={handleChange} placeholder="+91 00000 00000" className="w-full h-10 bg-[var(--slds-color-bg)] border border-[var(--slds-color-border)] rounded-[var(--slds-radius)] px-4 text-sm font-bold focus:border-[var(--slds-color-brand)] outline-none" />
                      </div>
                   </div>

                   <div className="space-y-3">
                      <label className="text-[12px] font-bold text-[var(--slds-color-text-secondary)] uppercase tracking-widest">Message</label>
                      <textarea name="message" value={form.message} onChange={handleChange} required rows={6} placeholder="Define the operational requirement or inquiry payload..." className="w-full bg-[var(--slds-color-bg)] border border-[var(--slds-color-border)] rounded-[var(--slds-radius)] p-4 text-sm font-medium focus:border-[var(--slds-color-brand)] outline-none resize-none leading-relaxed" />
                   </div>

                   <div className="pt-4">
                      <button 
                        type="submit" 
                        disabled={sending}
                        className="slds-button-brand h-12 px-12 font-bold text-sm uppercase tracking-widest flex items-center gap-3 shadow-[0_2px_8px_rgba(0,0,0,0.16)] disabled:opacity-50"
                      >
                         {sending ? 'Dispatching...' : 'Dispatch Signal'} <Send size={18} />
                      </button>
                   </div>
                </form>
              )}
           </div>

           <div className="lg:col-span-4 space-y-12">
              <div className="bg-white p-10 rounded-lg border border-[var(--slds-color-border)] shadow-[0_2px_8px_rgba(0,0,0,0.16)] space-y-10">
                 <h3 className="text-xl font-bold flex items-center gap-3"><Zap size={24} className="text-[var(--slds-color-brand)]" /> Quick Node Links</h3>
                 <div className="space-y-4">
                    {[
                      { icon: HelpCircle, label: 'Intelligence Support', href: '/support' },
                      { icon: LayoutDashboard, label: 'Enterprise Dashboard', href: '/dashboard' },
                      { icon: Users, label: 'Partnership Registry', href: '#' },
                    ].map((l, i) => (
                      <Link key={i} to={l.href} className="flex items-center justify-between p-4 rounded-lg border border-[var(--slds-color-border)] hover:bg-[#EBF5FF] hover:border-[var(--slds-color-brand)] transition-all group">
                         <div className="flex items-center gap-3">
                             <l.icon size={18} className="text-[var(--slds-color-text-secondary)] group-hover:text-[var(--slds-color-brand)]" />
                             <span className="text-[13px] font-bold tracking-tight text-[var(--slds-color-text-main)] uppercase">{l.label}</span>
                         </div>
                         <ChevronRight size={16} className="text-[var(--slds-color-text-secondary)] group-hover:text-[var(--slds-color-brand)] group-hover:translate-x-1 transition-all" />
                      </Link>
                    ))}
                 </div>

                 <div className="p-8 bg-[var(--slds-color-bg)] rounded-lg border border-[var(--slds-color-border)] text-center space-y-4">
                    <p className="text-[10px] font-bold uppercase text-[var(--slds-color-text-secondary)] tracking-widest">Response Time</p>
                    <p className="text-2xl font-bold text-[var(--slds-color-text-main)] tracking-tighter">{'<'} 24 Hours</p>
                    <p className="text-[12px] font-medium text-[var(--slds-color-text-secondary)]">Standard resolution time for all unclassified operational signals.</p>
                 </div>
              </div>

              <div className="flex items-start gap-5 px-6">
                 <div className="w-10 h-10 rounded-lg bg-[var(--slds-color-bg)] border border-[var(--slds-color-border)] flex items-center justify-center text-[var(--slds-color-brand)]"><MapPin size={20} /></div>
                 <div>
                    <h4 className="text-[14px] font-bold text-[var(--slds-color-text-main)] uppercase">Infrastructure HQ</h4>
                    <p className="text-[12px] font-medium text-[var(--slds-color-text-secondary)] mb-4">Waapcoders · Enterprise Operational Command</p>
                    <a href="mailto:info@waapcoders.in" className="text-[12px] font-bold text-[var(--slds-color-brand)] border-b border-[var(--slds-color-brand)]/20 hover:border-[var(--slds-color-brand)] transition-all uppercase tracking-widest">info@waapcoders.in</a>
                 </div>
              </div>
           </div>
        </section>
      </main>

      <WebsiteFooter />
    </div>
  );
}
